import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormModel } from '../../models/dynamic-form';
import { Metodosui } from '../../services/metodosui';
import { SeleccionardatosComponent } from '../seleccionardatos/seleccionardatos.component';
import { VerimagenComponent } from '../verimagen/verimagen.component';
import { MetodosgenService } from '../../services/metodosgen.service';
import { JavapiService } from '../../services/javapi.service';
import { firstValueFrom } from 'rxjs';
import { MeasureCalculatorComponent } from '../measure-calculator/measure-calculator.component';

@Component({
  selector: 'app-llenardatos',
  templateUrl: './llenardatos.component.html',
  styleUrls: ['./llenardatos.component.scss'],
  standalone: false
})
export class LlenardatosComponent implements OnInit {

  @Input() model: FormModel | undefined;
  sortedComponents: any[] = [];

  // Suggestions logic
  allSuggestions: { [key: string]: string[] } = {};
  filteredSuggestions: { [key: string]: string[] } = {};

  constructor(
    private modalCtrl: ModalController,
    private metodosUi: Metodosui,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService
  ) { }

  async ngOnInit() {


    // Set default date and handle suggestions
    if (this.model?.components?.date) {
      this.model.components.date.forEach((item: any) => {
        if (!item.value || item.value.trim() === '') {
          const now = new Date();
          const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
            .toISOString()
            .split('T')[0];
          item.value = localDate;
        }
      });
    }

    // Filter out Cancel/Close buttons as per user request (header close is sufficient)
    if (this.model?.components?.button) {
      this.model.components.button = this.model.components.button.filter((btn: any) => {
        const action = btn.action?.toLowerCase();
        const nombre = btn.nombre?.toLowerCase();
        return action !== 'cancel' && action !== 'close' && nombre !== 'cancelar';
      });
    }

    this.processComponents();

    // Re-check for inputs that need suggestions
    for (const comp of this.sortedComponents) {
      if (comp.__type === 'input' && comp.subrutina) {
        this.fetchSuggestions(comp);
      }
    }
  }

  async fetchSuggestions(item: any) {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonList(
          this.metodosGenService.setModeloGeneral(
            "genList", item.subrutina, '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data)) {
        // getCommonList returns string[] directly
        this.allSuggestions[item._uniqueId] = data.filter((v: any) => v);
      }
    } catch (e) {
      console.error('Error fetching suggestions for:', item.nombre, e);
    }
  }

  onInput(item: any, event: any) {
    const val = (event.target as any).value?.toLowerCase() || '';
    if (!val || val.length < 1) {
      this.filteredSuggestions[item._uniqueId] = [];
      return;
    }

    const suggestions = this.allSuggestions[item._uniqueId] || [];
    this.filteredSuggestions[item._uniqueId] = suggestions.filter((s: string) =>
      s.toLowerCase().includes(val) && s.toLowerCase() !== val
    ).slice(0, 5); // Limit to 5 suggestions
  }

  selectSuggestion(item: any, suggestion: string) {
    item.value = suggestion;
    this.filteredSuggestions[item._uniqueId] = [];
  }

  hasSuggestions(uniqueId: string): boolean {
    return !!(this.filteredSuggestions[uniqueId] && this.filteredSuggestions[uniqueId]!.length > 0);
  }

  async validateForm(): Promise<boolean> {
    for (const item of this.sortedComponents) {
      if (item.required === 'si') {
        const isEmpty = !item.value || (Array.isArray(item.value) && item.value.length === 0) || (typeof item.value === 'string' && item.value.trim() === '');

        if (isEmpty) {
          this.metodosUi.showToast(`El campo "${item.nombre || item.titulo}" es requerido.`, 'danger');
          return false;
        }
      }
    }
    return true;
  }


  processComponents() {
    if (!this.model?.components) return;

    let all: any[] = [];

    // Helper to generate IDs
    const generateId = (prefix: string, index: number) => `${prefix}_${index}_${Math.floor(Math.random() * 1000)}`;

    // Add inputs
    if (this.model.components.input) {
      all.push(...this.model.components.input.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'input';
        item._uniqueId = generateId('input', idx);
        return item;
      }));
    }
    // Add textareas
    if (this.model.components.textarea) {
      all.push(...this.model.components.textarea.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'textarea';
        item._uniqueId = generateId('textarea', idx);
        return item;
      }));
    }
    // Add dates
    if (this.model.components.date) {
      all.push(...this.model.components.date.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'date';
        item._uniqueId = generateId('date', idx);
        return item;
      }));
    }
    // Add selectOptions
    if (this.model.components.selectOption) {
      all.push(...this.model.components.selectOption.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'selectOption';
        item._uniqueId = generateId('selectOption', idx);
        return item;
      }));
    }
    // Add takePic
    if (this.model.components.takePic) {
      all.push(...this.model.components.takePic.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'takePic';
        item._uniqueId = generateId('takePic', idx);
        // Default to 'N' if not set
        if (!item.value) item.value = 'N';
        return item;
      }));
    }
    // Add toggle
    if (this.model.components.toggle) {
      all.push(...this.model.components.toggle.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'toggle';
        item._uniqueId = generateId('toggle', idx);
        // Ensure boolean-ish value
        if (item.value === undefined || item.value === null) item.value = true;
        return item;
      }));
    }

    // Add buttonmodal
    if (this.model.components.buttonmodal) {
      all.push(...this.model.components.buttonmodal.map((i: any, idx: number) => {
        const item = i as any;
        item.__type = 'buttonmodal';
        item._uniqueId = generateId('buttonmodal', idx);
        return item;
      }));
    }

    // Sort by ngmNumber
    this.sortedComponents = all.sort((a, b) => {
      const na = parseInt(a.ngmNumber) || 999;
      const nb = parseInt(b.ngmNumber) || 999;
      return na - nb;
    });

  }

  async handleTakePic(item: any) {
    const image = await this.metodosUi.takePicture();
    if (image?.base64String) {
      if (!item.imagesBase64) item.imagesBase64 = [];
      item.imagesBase64.push(image.base64String);
      item.value = 'Y';
      this.metodosUi.showToast('Imagen añadida', 'success');
    }
  }

  removePic(item: any, index: number) {
    if (item.imagesBase64 && item.imagesBase64.length > index) {
      item.imagesBase64.splice(index, 1);
      if (item.imagesBase64.length === 0) {
        item.value = 'N';
      }
      this.metodosUi.showToast('Imagen eliminada', 'secondary');
    }
  }

  async viewImage(img64: string) {
    await this.metodosUi.openModal(VerimagenComponent, { image: img64, title: 'Vista Previa de Imagen' });
  }

  async openSelectionModal(item: any) {
    // Open modal using MetodosUi
    const data = await this.metodosUi.openModal(
      SeleccionardatosComponent,
      {
        titulo: item.titulo,
        subrutina: item.subrutina,
        spr1: item.spr1,
        tipo: item.tipo,
        items: item.items || [], // Pass static items if present
        initialSelection: item.value, // Pass current values to pre-select
        hiddenCols: item.hiddenCols // Pass hidden columns config
      }
    );

    if (data && data.action === 'selected') {
      // Store the raw data value (array or object)
      item.value = data.data;
    }
  }

  // Helper to check if value is array for display
  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  // Helper to get display label (dt2) from an item
  getLabel(item: any): string {
    const val = item.value;
    if (!val) return '';

    if (this.isArray(val) && item.info_value) {
      return item.info_value.replace('$cantidad', val.length);
    }

    if (typeof val === 'string') return val;
    // User specifically asked for dt2
    return val.dt2 || val.nombre || val.name || JSON.stringify(val);
  }

  async openDynamicModal(item: any) {
    let component: any;
    let props: any = {};

    if (item.modal === 'MeasureCalculatorComponent') {
      component = MeasureCalculatorComponent;
      if (item.value && item.value.details) {
        props.initialDetails = item.value.details;
      }
      if (item.value && item.value.unit) {
        props.initialUnit = item.value.unit;
      }
    }

    if (!component) {
      console.warn('Unknown modal component:', item.modal);
      return;
    }

    const data = await this.metodosUi.openModal(component, props);

    // MetodosUi unwraps the dismiss data.
    // MeasureCalculator returns { totalArea, details } directly (no .data wrapper).
    // Other modals might return { action, data }.
    if (data) {
      let result = data;
      if (data.data) {
        result = data.data;
      }


      // Store full result in this button component's value for persistence
      item.value = result;

      // Update target component if specified
      if (item.target) {
        // Ensure lenient comparison for strings/numbers
        const targetComp = this.sortedComponents.find((c: any) => c.ngmNumber == item.target);
        if (targetComp) {
          if (result.totalArea !== undefined) {
            targetComp.value = result.totalArea;
          } else {
            targetComp.value = result;
          }
        } else {
          console.warn('Target component not found for ID:', item.target);
        }

        // Auto-set Unit to 'm2' (requested by user)
        // Search for a component named 'Unidad'
        const unitComp = this.sortedComponents.find(c =>
          (c.nombre?.toLowerCase() === 'unidad' || c.titulo?.toLowerCase() === 'unidad')
        );
        if (unitComp) {
          unitComp.value = 'm2';
        }
      }
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async handleAction(btn: any) {
    const action = btn.action;
    // Validate on actions (assuming non-cancel actions require validation)
    if (action !== 'cancel' && action !== 'close') {
      if (!(await this.validateForm())) {
        return;
      }
    }

    if (btn.isquestion && btn.question) {
      let message = btn.question;
      // Replace variables like $Lugar with the value of component called Lugar
      const matches = message.match(/\$[a-zA-Z0-9_]+/g);
      if (matches) {
        matches.forEach((match: string) => {
          const varName = match.substring(1).toLowerCase(); // remove $
          const comp = this.sortedComponents.find(c =>
            (c.nombre && c.nombre.toLowerCase() === varName)
          );
          if (comp) {
            let displayVal = '';
            const val = comp.value;

            if (val !== undefined && val !== null) {
              if (typeof val === 'object') {
                displayVal = this.getLabel(val);
              } else {
                displayVal = String(val);
              }

              // Format currency for money-related fields
              const moneyFields = ['monto', 'valor', 'total', 'precio'];
              if (moneyFields.includes(varName.toLowerCase()) && !isNaN(Number(val))) {
                displayVal = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val));
              }
            }
            message = message.replace(match, displayVal);
          }
        });
      }

      const confirmed = await this.metodosUi.showConfirmation('Confirmar Acción', message, btn.nombre || 'Aceptar');
      if (!confirmed) return;
    }

    // Pass back the action and the modified model/data
    this.modalCtrl.dismiss({
      action: action,
      data: this.model
    });
  }

}
